// src/contexts/KeyboardShortcutsContext.tsx
// Contexte pour gérer les raccourcis clavier globaux
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeyboardShortcuts, KeyboardShortcut, ShortcutGroup } from '../hooks/useKeyboardShortcuts';
import KeyboardShortcutsDialog from '../components/common/KeyboardShortcutsDialog';

interface KeyboardShortcutsContextType {
  openHelp: () => void;
  closeHelp: () => void;
  isHelpOpen: boolean;
  shortcutGroups: ShortcutGroup[];
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | null>(null);

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const openHelp = useCallback(() => setIsHelpOpen(true), []);
  const closeHelp = useCallback(() => setIsHelpOpen(false), []);

  // Définir les raccourcis globaux
  const shortcuts: KeyboardShortcut[] = useMemo(() => [
    // Aide
    {
      key: '?',
      shift: true,
      description: 'Afficher l\'aide des raccourcis',
      category: 'Général',
      action: openHelp,
    },
    {
      key: 'Escape',
      description: 'Fermer le dialog / Annuler',
      category: 'Général',
      action: closeHelp,
    },

    // Navigation
    {
      key: 'h',
      ctrl: true,
      description: 'Aller au Dashboard',
      category: 'Navigation',
      action: () => navigate('/'),
    },
    {
      key: 'p',
      ctrl: true,
      shift: true,
      description: 'Aller aux Posts',
      category: 'Navigation',
      action: () => navigate('/posts'),
    },
    {
      key: 'u',
      ctrl: true,
      shift: true,
      description: 'Aller aux Utilisateurs',
      category: 'Navigation',
      action: () => navigate('/users'),
    },
    {
      key: 'e',
      ctrl: true,
      shift: true,
      description: 'Aller aux Églises',
      category: 'Navigation',
      action: () => navigate('/churches'),
    },

    // Recherche
    {
      key: 'k',
      ctrl: true,
      description: 'Ouvrir la recherche',
      category: 'Actions',
      action: () => {
        const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
        if (searchInput) searchInput.focus();
      },
    },
    {
      key: '/',
      description: 'Focus sur la recherche',
      category: 'Actions',
      action: () => {
        const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
        if (searchInput) searchInput.focus();
      },
    },

    // Actions
    {
      key: 'n',
      ctrl: true,
      description: 'Nouveau post',
      category: 'Actions',
      action: () => {
        const createButton = document.querySelector('[data-create-post]') as HTMLButtonElement;
        if (createButton) createButton.click();
      },
    },
    {
      key: 'r',
      ctrl: true,
      description: 'Rafraîchir',
      category: 'Actions',
      action: () => {
        const refreshButton = document.querySelector('[data-refresh]') as HTMLButtonElement;
        if (refreshButton) refreshButton.click();
      },
    },

    // Raccourcis spéciaux
    {
      key: 'ArrowLeft',
      alt: true,
      description: 'Page précédente',
      category: 'Navigation',
      action: () => navigate(-1),
    },
    {
      key: 'ArrowRight',
      alt: true,
      description: 'Page suivante',
      category: 'Navigation',
      action: () => navigate(1),
    },
  ], [navigate, openHelp, closeHelp]);

  // Grouper les raccourcis par catégorie pour l'affichage
  const shortcutGroups: ShortcutGroup[] = useMemo(() => {
    const groups: Record<string, ShortcutGroup> = {};

    shortcuts.forEach((shortcut) => {
      if (!groups[shortcut.category]) {
        groups[shortcut.category] = {
          category: shortcut.category,
          shortcuts: [],
        };
      }
      groups[shortcut.category].shortcuts.push({
        key: shortcut.key,
        ctrl: shortcut.ctrl,
        shift: shortcut.shift,
        alt: shortcut.alt,
        meta: shortcut.meta,
        description: shortcut.description,
      });
    });

    // Ordre des catégories
    const categoryOrder = ['Général', 'Navigation', 'Actions'];
    return categoryOrder
      .filter((cat) => groups[cat])
      .map((cat) => groups[cat]);
  }, [shortcuts]);

  // Activer les raccourcis
  useKeyboardShortcuts({
    shortcuts,
    enabled: true,
    ignoreInputs: true,
  });

  const value: KeyboardShortcutsContextType = {
    openHelp,
    closeHelp,
    isHelpOpen,
    shortcutGroups,
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
      <KeyboardShortcutsDialog
        open={isHelpOpen}
        onClose={closeHelp}
        shortcutGroups={shortcutGroups}
      />
    </KeyboardShortcutsContext.Provider>
  );
};

export const useKeyboardShortcutsContext = (): KeyboardShortcutsContextType => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within a KeyboardShortcutsProvider');
  }
  return context;
};

export default KeyboardShortcutsProvider;
